import React, { useMemo } from 'react';
import { Question, QuestionScores, Entity } from '../types';
import EntityList from './EntityList';
import AnnotationCard from './AnnotationCard';
import AnnotatedText from './AnnotatedText';
import AddExampleSeparator from './AddExampleSeparator';
import { ratingToCategory } from '../utils/scoreUtils';
import { formatTime } from '../utils/inferenceUtils';

interface ComparisonViewProps {
  questions: Question[];
  modelNames: string[];
  scores: QuestionScores;
  onScoreUpdate: (questionId: string, modelName: string, score: any) => void;
  onAnnotationsUpdate: (questionId: string, annotations: Entity[]) => void;
  allowAddExamples?: boolean;
  customLabelColors?: Record<string, string>;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({
  questions,
  modelNames,
  scores,
  onScoreUpdate,
  onAnnotationsUpdate,
  allowAddExamples = false,
  customLabelColors
}) => {
  // Extract all unique entity types from all models
  const availableEntityTypes = useMemo(() => {
    const types = new Set<string>();
    questions.forEach(question => {
      question.modelResponses.forEach(response => {
        response.entities.forEach(entity => {
          types.add(entity.label);
        });
      });
      // Also include entity types from human annotations
      question.humanAnnotations?.forEach(entity => {
        types.add(entity.label);
      });
    });
    // Also include any custom labels that have been created but not yet used
    if (customLabelColors) {
      Object.keys(customLabelColors).forEach(label => {
        types.add(label);
      });
    }
    return Array.from(types).sort();
  }, [questions, customLabelColors]);

  const getModelResponse = (question: Question, modelName: string) => {
    return question.modelResponses.find(response => response.modelName === modelName);
  };

  const getQuestionScore = (questionId: string, modelName: string) => {
    return scores[questionId]?.[modelName];
  };

  const getScoreColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    if (rating >= 2) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Top separator on background */}
      {
        <div className="px-4">
          <AddExampleSeparator
            disabled={!allowAddExamples}
            disabledTooltip="add new disabled - change in settings"
            onAdd={(text) => {
              const event = new CustomEvent('add-example', { detail: { text, insertAfterId: null } });
              window.dispatchEvent(event);
            }}
          />
        </div>
      }

      {questions.map((question, index) => {
        const questionScores = scores[question.id] || {};
        const questionNumber = index + 1;
        
        return (
          <div
            key={question.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200"
          >
            {/* Question Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-gray-500">#{questionNumber}</span>
                    <h3 className="text-lg font-medium text-gray-900">
                      <AnnotatedText
                        text={question.text}
                        annotations={question.humanAnnotations || []}
                        availableEntityTypes={availableEntityTypes}
                        onChange={(annotations) => onAnnotationsUpdate(question.id, annotations)}
                        className="inline"
                        customLabelColors={customLabelColors}
                      />
                    </h3>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  {Object.entries(questionScores).map(([modelName, score]) => (
                    <div
                      key={modelName}
                      className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(score.rating)}`}
                    >
                      {modelName}: {score.rating}/5
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Model Responses */}
            <div className="p-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                {/* Human Annotation Card (expand to fill available width) */}
                <div className="col-span-full">
                  <AnnotationCard
                    questionText={question.text}
                    annotations={question.humanAnnotations || []}
                    availableEntityTypes={availableEntityTypes}
                    onAnnotationsChange={(annotations) => onAnnotationsUpdate(question.id, annotations)}
                    customLabelColors={customLabelColors}
                  />
                </div>
                
                {/* Model Cards */}
                {modelNames.map((modelName) => {
                  const response = getModelResponse(question, modelName);
                  const score = getQuestionScore(question.id, modelName) || {
                    rating: 0,
                    category: 'Fair' as const,
                    notes: ''
                  };
                  
                  const derivedCategory = score.rating > 0 ? ratingToCategory(score.rating) : null;
                  return (
                    <div
                      key={modelName}
                      className={`p-3 rounded-lg border overflow-hidden ${
                        derivedCategory === 'Best' ? 'bg-green-50 border-green-200' :
                        derivedCategory === 'Good' ? 'bg-blue-50 border-blue-200' :
                        derivedCategory === 'Fair' ? 'bg-yellow-50 border-yellow-200' :
                        derivedCategory === 'Poor' ? 'bg-red-50 border-red-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Model Header */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{modelName}</h4>
                          {score.rating > 0 && (
                            <div className={`text-sm font-medium ${getScoreColor(score.rating)}`}>
                              {score.rating}/5
                            </div>
                          )}
                        </div>
                        {response?.inferenceTime !== undefined && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatTime(response.inferenceTime)}
                          </div>
                        )}
                      </div>
                      
                      {/* Entities */}
                      <div className="mb-3">
                        {response ? (
                          <EntityList entities={response.entities} customLabelColors={customLabelColors} />
                        ) : (
                          <p className="text-gray-500 text-sm">No response found</p>
                        )}
                      </div>
                      
                      {/* Inline Scoring */}
                      <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                        {/* Star Rating */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">Rating:</span>
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                key={rating}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onScoreUpdate(question.id, modelName, { ...score, rating });
                                }}
                                className={`w-6 h-6 rounded-full text-xs font-medium transition-colors ${
                                  score.rating === rating
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                title={`Rate ${rating} out of 5`}
                              >
                                {rating}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                      {/* Category removed: derived from rating */}
                        
                        {/* Notes */}
                        <div>
                          <textarea
                            value={score.notes}
                            onChange={(e) => {
                              e.stopPropagation();
                              onScoreUpdate(question.id, modelName, { ...score, notes: e.target.value });
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Add notes..."
                            className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {/* Bottom separator on background (after last card) */}
      {questions.length > 0 && (
        <div className="px-4">
          <AddExampleSeparator
            disabled={!allowAddExamples}
            disabledTooltip="add new disabled - change in settings"
            onAdd={(text) => {
              const lastId = questions[questions.length - 1].id;
              const event = new CustomEvent('add-example', { detail: { text, insertAfterId: lastId } });
              window.dispatchEvent(event);
            }}
          />
        </div>
      )}
      
      {questions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No questions found. Try adjusting your search terms.</p>
        </div>
      )}
    </div>
  );
};

export default ComparisonView;
